import { ChevronDown, Pencil, Plus } from 'lucide-react'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'

import { useCenterStore } from '@caradasstore/CenterStore'
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { updateAssignee } from '../../api/api'
import { Logger } from '../../logger'
import { filterAssigneeUsers, withCurrentAssignee } from './AssignedUser'

const getInitials = (name) => {
  if (!name) return ''
  const parts = String(name).trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return ''
  if (parts.length === 1) return (parts[0][0] || '').toUpperCase()
  return ((parts[0][0] || '') + (parts[parts.length - 1][0] || '')).toUpperCase()
}

/** Avatar-style assignee picker shared by the Quotes Queue and Quote Summary pages.
 *  Renders a circular initials chip + chevron (or a dashed-circle placeholder when
 *  unassigned), with an inline dropdown built from the same logic as the legacy
 *  AssignedUser on staging:
 *   1. Filter userList by `!is_admin` (or `is_admin` when isAdminUsersOnly is set),
 *      never offering client users (CAR-4080).
 *   2. Self-heal: if the current assignee was filtered out (an admin, or a client user
 *      assigned before CAR-4080), push them back so the row still shows who it is.
 *   3. Sort options alphabetically by name.
 *   4. Append "Unassigned" only when !isOpenStatus.
 *  The menu renders in a portal (Radix Popover) so a list's overflow-auto table wrapper
 *  cannot clip it on the last rows (CAR-4080).
 *  Persists via updateAssignee. Pass `showName` to render the assignee's full name
 *  next to the avatar (used in the summary header strip), and `label` (e.g. "Assignee:") to
 *  caption it - the header next to the Actions menu does, for every role. */
export const AssigneeAvatarPicker = ({
  workOrderId,
  initialAssignee,
  closedStatus = false,
  isOpenStatus = false,
  isAdminUsersOnly = false,
  showName = false,
  showEditIcon = false,
  label,
  type = 'quotes'
}) => {
  const userList = useCenterStore((store) => store.userList)
  // Picking an assignee writes, so the avatar stops being a control for a client user.
  const isReadOnly = useIsReadOnly()
  const [assigneeId, setAssigneeId] = useState(initialAssignee?.id ?? null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    setAssigneeId(initialAssignee?.id ?? null)
  }, [initialAssignee?.id])

  // Look up in userList first (freshest source); fall back to initialAssignee so
  // admin-assigned rows still render even when admins are excluded from userList.
  const assignee = userList.find((u) => u.id === assigneeId) || (initialAssignee?.id === assigneeId ? initialAssignee : null)
  const initials = getInitials(assignee?.name)

  const onPick = async (userId) => {
    try {
      const res = await updateAssignee({ id: workOrderId, userId: userId || 0, type })
      if (res?.status === 200) {
        setAssigneeId(userId || null)
        setOpen(false)
      }
    } catch (err) {
      Logger.error({ message: err, payload: { file: 'AssigneeAvatarPicker', workOrderId, type } })
    }
  }

  // Closed records - and, via closedStatus, a client user's view of any record - show the
  // assignee as text. The label still applies, or the name would stand there unexplained.
  if (closedStatus) {
    return (
      <span className='inline-flex items-center gap-1.5 text-[13px] text-[#6B7280]'>
        {label && <span className='text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'>{label}</span>}
        {assignee?.name || 'Unassigned'}
      </span>
    )
  }

  // Build the pickable list using staging's AssignedUser semantics.
  const pickableUsers = withCurrentAssignee(
    filterAssigneeUsers(userList, { isAdminUsersOnly }),
    initialAssignee && assigneeId === initialAssignee.id ? initialAssignee : null
  ).sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()))

  // A client user (read-only) cannot reassign, so there is no picker - just who is assigned,
  // labelled, since a bare name next to an avatar says nothing about what it is.
  if (isReadOnly) {
    const assigneeLabel = assignee?.name ? `Assignee: ${assignee.name}` : 'Unassigned'
    return (
      <span className='inline-flex items-center gap-1.5' title={assigneeLabel} aria-label={assigneeLabel}>
        {label && <span className='text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'>{label}</span>}
        {initials ? (
          <span
            className={`inline-flex items-center justify-center rounded-full bg-[#E8EFFF] text-[11px] font-bold text-[#0A1628] ${
              showName ? 'h-7 w-7' : 'h-9 w-9 text-[12px]'
            }`}
          >
            {initials}
          </span>
        ) : (
          !showName && <span className='text-[13px] text-[#6B7280]'>—</span>
        )}
        {showName && <span className='text-[14px] font-medium text-[#0A1628]'>{assignee?.name || 'Unassigned'}</span>}
      </span>
    )
  }

  return (
    <div className='inline-flex items-center gap-1.5'>
      {label && <span className='text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'>{label}</span>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type='button'
            className='inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity'
            aria-label={assignee?.name ? `Assignee ${assignee.name}` : 'Unassigned — pick an assignee'}
          >
            {initials ? (
              <span
                className={`inline-flex items-center justify-center rounded-full bg-[#E8EFFF] text-[11px] font-bold text-[#0A1628] ${
                  showName ? 'h-7 w-7' : 'h-9 w-9 text-[12px]'
                }`}
              >
                {initials}
              </span>
            ) : (
              <span
                className={`inline-flex items-center justify-center rounded-full border border-dashed border-[#6B7280] text-[#6B7280] ${
                  showName ? 'h-7 w-7' : 'h-9 w-9'
                }`}
              >
                <Plus className='h-4 w-4' />
              </span>
            )}
            {showName && assignee?.name && <span className='text-[14px] font-medium text-[#0A1628]'>{assignee.name}</span>}
            {showEditIcon ? <Pencil className='h-3.5 w-3.5 text-[#1F6FEB]' /> : <ChevronDown className='h-4 w-4 text-[#6B7280]' />}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align='start'
          sideOffset={8}
          // Portalled, but React still bubbles clicks up the component tree - keep a pick
          // from also triggering the list row's navigate-on-click.
          onClick={(e) => e.stopPropagation()}
          className='w-56 max-h-72 overflow-auto rounded-lg border-[#D1D5D8] bg-white p-1 shadow-lg'
        >
          {pickableUsers.length === 0 ? (
            <div className='px-3 py-2 text-[13px] text-[#6B7280]'>No users available</div>
          ) : (
            pickableUsers.map((u) => (
              <button
                key={u.id}
                type='button'
                onClick={() => onPick(u.id)}
                className={`block w-full rounded px-3 py-2 text-left text-[13px] hover:bg-[#F8FAFC] ${
                  u.id === assigneeId ? 'bg-[#E8EFFF] font-semibold text-[#0A1628]' : 'text-[#0A1628]'
                }`}
              >
                {u.name}
              </button>
            ))
          )}
          {!isOpenStatus && (
            <>
              <div className='my-1 border-t border-[#EBEDF0]' />
              <button
                type='button'
                onClick={() => onPick(0)}
                className='block w-full rounded px-3 py-2 text-left text-[13px] text-[#6B7280] hover:bg-[#F8FAFC]'
              >
                Unassigned
              </button>
            </>
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}

AssigneeAvatarPicker.propTypes = {
  workOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialAssignee: PropTypes.object,
  closedStatus: PropTypes.bool,
  isOpenStatus: PropTypes.bool,
  isAdminUsersOnly: PropTypes.bool,
  showName: PropTypes.bool,
  showEditIcon: PropTypes.bool,
  label: PropTypes.string,
  type: PropTypes.string
}
